import { useState, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import useGroups from "admin/hooks/useGroups";
import useRoles from "admin/hooks/useRoles";
import useUsers from "admin/hooks/useUsers";
import User from "admin/types/interfaces/user";
import { canAssignRole, sortFunctionalRoles } from "admin/utils/roles";
import { AlertCircle, Loader2 } from "lucide-react";
import pluralize from "pluralize";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { ACCESS_CONTROLLER_V1 } from "~/utils/accessController";
import { apiMutate, extractErrorMessage, isApiError, parseApiErrorBody } from "~/utils/apiClient";

const buildGroupMap = (users: User[]): Record<string, string[]> => {
  // Map of { groupName: [userEmail, ...] }
  return users.reduce((acc: Record<string, string[]>, user) => {
    user.tags?.forEach((group) => {
      acc[group.name] = [...(acc[group.name] || []), user.email];
    });
    return acc;
  }, {});
};

interface BulkEditUsersFormData {
  groups: Record<string, string[]>;
  roles: Record<string, boolean | "indeterminate">;
}

interface BulkEditUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUsers: User[];
  onJobStarted: (jobId: string, totalUsers: number) => void;
}

type BulkEditTab = "roles" | "groups";

export function BulkEditUsersModal({
  isOpen,
  onClose,
  selectedUsers,
  onJobStarted,
}: BulkEditUsersModalProps) {
  const [activeTab, setActiveTab] = useState<BulkEditTab>("roles");
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoadingInitialGroupMap, setIsLoadingInitialGroupMap] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { mutate } = useUsers();

  const { data: allGroups = [] } = useGroups();
  const { data: roles } = useRoles();

  const [initialGroupMap, setInitialGroupMap] = useState<Record<string, string[]>>(() =>
    buildGroupMap(selectedUsers)
  );
  const [initialRolesState, setInitialRolesState] = useState<
    Record<string, boolean | "indeterminate">
  >({});

  const sortedRoles = useMemo(() => [...(roles ?? [])].sort(sortFunctionalRoles), [roles]);

  const { control, handleSubmit, reset, watch } = useForm<BulkEditUsersFormData>({
    defaultValues: {
      groups: initialGroupMap,
      roles: {},
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (!isOpen || !roles) return;
    setActiveTab("roles");
    setSubmitError(null);
    // Refetch users on open so group memberships reflect any changes since the last save
    setIsLoadingInitialGroupMap(true);
    mutate().then((freshUsers) => {
      const selectedEmails = new Set(selectedUsers.map((u) => u.email));
      const freshSelectedUsers = (freshUsers ?? []).filter((u) => selectedEmails.has(u.email));
      const freshMap = buildGroupMap(freshSelectedUsers);

      const freshRolesState = roles.reduce(
        (acc, role) => {
          const count = freshSelectedUsers.filter((u) =>
            u.roles.some((r) => r.id === role.id)
          ).length;
          acc[role.id] =
            count === 0 ? false : count === freshSelectedUsers.length ? true : "indeterminate";
          return acc;
        },
        {} as Record<string, boolean | "indeterminate">
      );

      setInitialGroupMap(freshMap);
      setInitialRolesState(freshRolesState);
      reset({ groups: freshMap, roles: freshRolesState });
      setIsLoadingInitialGroupMap(false);
    });
    // selectedUsers intentionally excluded from dependencies: selection doesn't change while the
    // modal is open, and we don't want to re-run if the parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, roles]);

  const getRoleHelperText = (
    roleId: string,
    currentRoles: Record<string, boolean | "indeterminate">
  ): string => {
    const current = currentRoles[roleId];
    const initial = initialRolesState[roleId];
    if (current === true && initial !== true) {
      const willAdd = selectedUsers.filter((u) => !u.roles.some((r) => r.id === roleId)).length;
      return `${willAdd} ${pluralize("user", willAdd)} will have this role added`;
    }
    if (current === false && initial !== false) {
      const willRemove = selectedUsers.filter((u) => u.roles.some((r) => r.id === roleId)).length;
      return `${willRemove} ${pluralize("user", willRemove)} will have this role removed`;
    }
    const count = selectedUsers.filter((u) => u.roles.some((r) => r.id === roleId)).length;
    return count > 0
      ? `${count} ${pluralize("user", count)} ${count > 1 ? "have" : "has"} this role`
      : "";
  };

  const onSubmit = async (data: BulkEditUsersFormData) => {
    setIsUpdating(true);
    setSubmitError(null);
    const { groups: currentGroups, roles: currentRoles } = data;

    const add_tags = allGroups
      .filter(
        (g) => isGroupModified(g.name, currentGroups) && (currentGroups[g.name]?.length ?? 0) > 0
      )
      .map((g) => g.name);
    const remove_tags = allGroups
      .filter(
        (g) => isGroupModified(g.name, currentGroups) && (currentGroups[g.name]?.length ?? 0) === 0
      )
      .map((g) => g.name);
    const add_roles = Object.entries(currentRoles)
      .filter(([id, val]) => val === true && initialRolesState[id] !== true)
      .map(([id]) => id);
    const remove_roles = Object.entries(currentRoles)
      .filter(([id, val]) => val === false && initialRolesState[id] !== false)
      .map(([id]) => id);

    if (!add_tags.length && !remove_tags.length && !add_roles.length && !remove_roles.length) {
      setIsUpdating(false);
      handleClose();
      return;
    }

    try {
      const { job_id, total_users } = await apiMutate<{
        job_id: string;
        total_users: number;
      }>(`${ACCESS_CONTROLLER_V1}/users/bulk-jobs`, "PATCH", {
        user_emails: selectedUsers.map((u) => u.email),
        add_roles,
        remove_roles,
        add_tags,
        remove_tags,
      });
      onJobStarted(job_id, total_users);
      setIsUpdating(false);
      onClose();
    } catch (error) {
      const body = isApiError(error) ? parseApiErrorBody(error) : null;
      setSubmitError(extractErrorMessage(body) ?? "Failed to update users. Please try again.");
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    if (isUpdating) {
      return;
    }
    reset();
    setSubmitError(null);
    onClose();
  };

  const getCheckedState = (userEmails: string[]) => {
    if (userEmails.length === 0) {
      return false;
    }
    if (userEmails.length === selectedUsers.length) {
      return true;
    }
    return "indeterminate";
  };

  const getHelperText = (
    groupName: string,
    currentGroups: Record<string, string[] | undefined> | undefined
  ) => {
    const currentNumberOfUsers = currentGroups?.[groupName]?.length ?? 0;
    const previousNumberOfUsers = initialGroupMap[groupName]?.length ?? 0;
    const numberOfUsersToDisplay =
      currentNumberOfUsers > 0 ? currentNumberOfUsers : previousNumberOfUsers;
    const userText = `${
      numberOfUsersToDisplay === selectedUsers.length ? "All" : numberOfUsersToDisplay
    } ${pluralize("user", numberOfUsersToDisplay)}`;

    if (isGroupModified(groupName, currentGroups)) {
      return `${userText} will be ${
        currentNumberOfUsers > previousNumberOfUsers ? "added to" : "removed from"
      } this group`;
    }
    if (!currentNumberOfUsers) {
      return "";
    }
    return `${userText} ${currentNumberOfUsers > 1 ? "are" : "is"} in this group`;
  };

  const isGroupModified = (
    groupName: string,
    currentGroups: Record<string, string[] | undefined> | undefined
  ) => {
    const initial = (initialGroupMap[groupName] ?? []).slice().sort();
    const current = (currentGroups?.[groupName] ?? []).slice().sort();
    if (initial.length !== current.length) {
      return true;
    }
    return initial.some((email, i) => email !== current[i]);
  };

  const watchedGroups = watch("groups");
  const watchedRoles = watch("roles");
  const hasChanges = useMemo(() => {
    const currentGroups = watchedGroups ?? {};
    const currentRoles = watchedRoles ?? {};
    const add_tags = allGroups.filter(
      (g) => isGroupModified(g.name, currentGroups) && (currentGroups[g.name]?.length ?? 0) > 0
    );
    const remove_tags = allGroups.filter(
      (g) => isGroupModified(g.name, currentGroups) && (currentGroups[g.name]?.length ?? 0) === 0
    );
    const add_roles = Object.entries(currentRoles).filter(
      ([id, val]) => val === true && initialRolesState[id] !== true
    );
    const remove_roles = Object.entries(currentRoles).filter(
      ([id, val]) => val === false && initialRolesState[id] !== false
    );
    return (
      add_tags.length > 0 ||
      remove_tags.length > 0 ||
      add_roles.length > 0 ||
      remove_roles.length > 0
    );
  }, [watchedGroups, watchedRoles, initialGroupMap, initialRolesState, allGroups]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-6xl max-h-[90vh] bg-background flex flex-col p-0"
        onWheel={(e) => {
          e.stopPropagation();
        }}
        onTouchMove={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Bulk Edit Users ({selectedUsers.length})</DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0">
          {isLoadingInitialGroupMap ? (
            <div className="flex items-center justify-center py-12 px-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <form className="flex flex-col flex-1 min-h-0">
              <Tabs
                value={activeTab}
                onValueChange={(value) => setActiveTab(value as BulkEditTab)}
                className="flex flex-col flex-1 min-h-0"
              >
                <div className="flex justify-start shrink-0 px-6">
                  <TabsList className="w-fit">
                    <TabsTrigger value="roles">Functional Roles</TabsTrigger>
                    <TabsTrigger value="groups">Groups</TabsTrigger>
                  </TabsList>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <TabsContent value="roles" className="space-y-4 mt-2 px-6">
                    <Controller
                      name="roles"
                      control={control}
                      render={({ field }) => (
                        <div className="flex flex-col divide-y divide-border">
                          {sortedRoles.map((role) => {
                            const helperText = getRoleHelperText(role.id, field.value);
                            const isAssignable = canAssignRole(role);
                            return (
                              <div
                                key={role.id}
                                className="flex gap-3 py-3 first:border-t last:pb-0"
                              >
                                <Checkbox
                                  id={`role-${role.id}`}
                                  disabled={!isAssignable}
                                  checked={field.value?.[role.id] ?? false}
                                  onCheckedChange={(checked) => {
                                    if (checked === "indeterminate") return;
                                    field.onChange({
                                      ...field.value,
                                      [role.id]: checked,
                                    });
                                  }}
                                />
                                <div className="flex flex-col gap-1">
                                  <Label className="leading-[18px]" htmlFor={`role-${role.id}`}>
                                    {role.name}
                                    <p className="text-xs text-muted-foreground">{helperText}</p>
                                  </Label>
                                  {role.description && (
                                    <p className="text-xs text-muted-foreground">
                                      {role.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    />
                  </TabsContent>
                  <TabsContent value="groups" className="space-y-4 mt-2 px-6">
                    <Controller
                      name="groups"
                      control={control}
                      render={({ field }) => (
                        <div className="flex flex-col divide-y divide-border">
                          {allGroups.map((group) => (
                            <div
                              key={group.name}
                              className="flex gap-3 py-3 first:border-t last:pb-0"
                            >
                              <Checkbox
                                id={group.name}
                                checked={
                                  field.value?.[group.name]
                                    ? getCheckedState(field.value[group.name])
                                    : false
                                }
                                onCheckedChange={(checked) => {
                                  if (checked === "indeterminate") {
                                    return;
                                  }
                                  const nextGroups = { ...(field.value ?? {}) };
                                  nextGroups[group.name] = checked
                                    ? selectedUsers.map((user) => user.email)
                                    : [];
                                  field.onChange(nextGroups);
                                }}
                              />
                              <div className="flex flex-col gap-1">
                                <Label className="leading-[18px]" htmlFor={group.name}>
                                  {group.display_name || group.name}
                                  <p className="text-xs text-muted-foreground">
                                    {getHelperText(group.name, field.value)}
                                  </p>
                                </Label>
                                <p className="text-xs text-muted-foreground">{group.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    />
                  </TabsContent>
                </div>
              </Tabs>
            </form>
          )}
        </div>

        {submitError && (
          <div className="px-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          </div>
        )}

        <div className="flex justify-end gap-4 px-6 py-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isUpdating}
            size="lg"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="lg"
            disabled={isUpdating || isLoadingInitialGroupMap || !hasChanges}
            className="min-w-[96px]"
            onClick={handleSubmit(onSubmit)}
          >
            {isUpdating ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
